import { CAISO_UNCONSTRAINED_SENTINEL_MW } from "./busFilterTypes";
import {
    GeneratorType,
    LOAD_TYPE,
    SOLAR_TYPE,
    STORAGE_TYPE,
    WIND_TYPE
} from "./generatorType";
import { RegionType } from "./regionType";
import {
    CAISO_PRE_SCREEN_CLUSTER_16_SCOPE_TYPE,
    ScopeName,
    WECC_ALL_GENERATORS_SCOPE_TYPE,
    WECC_BASE_SCOPE_TYPE,
    WECC_FUTURE_TERM_ALL_GENERATORS_SCOPE_TYPE,
    WECC_FUTURE_TERM_BASE_SCOPE_TYPE,
    WECC_FUTURE_TERM_LATE_STAGE_GENERATORS_SCOPE_TYPE,
    WECC_LATE_STAGE_GENERATORS_SCOPE_TYPE
} from "./scope";

export const getComponentConfig = (
    selectedRegion: RegionType,
    selectedGeneratorType: GeneratorType
): ComponentConfig => {
    const maybeConfig =
        REGIONS_TO_COMPONENT_CONFIG[selectedRegion][selectedGeneratorType];

    if (maybeConfig === undefined) {
        throw Error(
            `Unexpected combination: ${selectedRegion}, ${selectedGeneratorType}`
        );
    }

    return maybeConfig;
};

// TODO: At some point, we may want separate config objects for the sidebar and the detail pane
export type ComponentConfig = PricedConstraintConfig | CurtailmentConfig;

export type CapacityColorSliderConfig = {
    readonly max: number;
    readonly labelStepSize: number;
    readonly stepSize: number;
};

const STANDARD_CAPACITY_COLOR_SLIDER: CapacityColorSliderConfig = {
    max: 500,
    labelStepSize: 100,
    stepSize: 10
};
const LOAD_CAPACITY_COLOR_SLIDER: CapacityColorSliderConfig = {
    max: 1000,
    labelStepSize: 200,
    stepSize: 10
};
const CAISO_CAPACITY_COLOR_SLIDER: CapacityColorSliderConfig = {
    max: 2000,
    labelStepSize: 500,
    stepSize: 10
};
const CAISO_PRIORITIZED_SCOPE_NAMES: ReadonlyArray<ScopeName> = [
    CAISO_PRE_SCREEN_CLUSTER_16_SCOPE_TYPE
];

const WECC_PRIORITIZED_SCOPE_NAMES: ReadonlyArray<ScopeName> = [
    WECC_BASE_SCOPE_TYPE,
    WECC_LATE_STAGE_GENERATORS_SCOPE_TYPE,
    WECC_ALL_GENERATORS_SCOPE_TYPE,
    WECC_FUTURE_TERM_BASE_SCOPE_TYPE,
    WECC_FUTURE_TERM_LATE_STAGE_GENERATORS_SCOPE_TYPE,
    WECC_FUTURE_TERM_ALL_GENERATORS_SCOPE_TYPE
];

// English descriptions shown in the capacity legend instead of numeric
export type CapacityLegendLabels = {
    readonly high: string;
    readonly medium: string;
    readonly low: string;
};

// A deliverable (energy) size equal to `sentinelMw` has a pill that shows
// `label` instead of the numeric MW value. `sentinelMw` is a backend flag
// meaning "no constraints", not a real capacity — see
// CAISO_UNCONSTRAINED_SENTINEL_MW.
export type NoConstraintsDeliverableConfig = {
    readonly sentinelMw: number;
    readonly label: string;
};
const CAISO_NO_CONSTRAINTS_DELIVERABLE: NoConstraintsDeliverableConfig = {
    sentinelMw: CAISO_UNCONSTRAINED_SENTINEL_MW,
    label: "No C16 intake constraints"
};

type BaseConfig = {
    readonly showContingency: boolean;
    // Show the ERCOT-only "Contingency Type" and "Contingency Definition"
    // columns (and label the contingency column "Contingency Name"). Other
    // regions omit this and keep the original single "Contingency" column.
    readonly showContingencyDetails?: boolean;
    readonly showLineTaps: boolean;
    readonly missingBranchMetadata: boolean;
    readonly hideResourceType: boolean;
    // only relevant for storage generator types and for PJM, we don't want users to change the charging size separately
    readonly showChargingSize: boolean;
    readonly showOwnersFilter: boolean;
    readonly capacityColorSliderConfig: CapacityColorSliderConfig;
    readonly hideCapacityColorSlider?: boolean;
    // English descriptions for the capacity legend rows, in place of numeric thresholds.
    readonly capacityLegendLabels?: CapacityLegendLabels;
    readonly noConstraintsDeliverable?: NoConstraintsDeliverableConfig;
    readonly hasPlannedInterconnectionLocations?: boolean;
    // Region-specific (WECC/RTOE): show the "Hide RTOE buses" toggle in the
    // filters "Other" section (see OutOfRegionBusFilter / isOutOfRegionBus).
    readonly showOutOfRegionBusFilter?: boolean;
    // Pin scopes in array order; unlisted scopes remain alphabetical.
    readonly prioritizedScopeNames?: ReadonlyArray<ScopeName>;
    readonly showImpactPercent?: boolean;
};

export type PricedConstraintConfig = BaseConfig & {
    readonly showCapacity: boolean;
    readonly hasAllocatedCost: boolean;
    readonly isPreExistingConstraintsEnabled: boolean;
    readonly allowUsersToViewLowConfidenceBuses: boolean;
    readonly allowCapacityToggleInCostGraph?: boolean;
    readonly showChargingCostGraph?: boolean;
};

export const isPricedConstraintConfig = (
    type: ComponentConfig
): type is PricedConstraintConfig => {
    return (type as PricedConstraintConfig).showCapacity !== undefined;
};

export type CurtailmentConfig = BaseConfig & {
    readonly showModelSwitcherConstraintTable: boolean;
    readonly showModelColumnConstraintTable: boolean;
    readonly allowUsersToViewLowConfidenceBuses: boolean;
};

export const isCurtailmentConfig = (
    type: ComponentConfig
): type is CurtailmentConfig => {
    return (
        (type as CurtailmentConfig).showModelSwitcherConstraintTable !==
        undefined
    );
};

export const convertToCurtailmentConfig = (
    config: ComponentConfig
): CurtailmentConfig => {
    if (isCurtailmentConfig(config)) {
        return config;
    }
    throw new Error("Unexpected config type");
};

const NYISO_CONFIG = {
    showModelSwitcherConstraintTable: false,
    showModelColumnConstraintTable: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER
};
const PJM_CONFIG = {
    showCapacity: true,
    hasAllocatedCost: true,
    isPreExistingConstraintsEnabled: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: false,
    showOwnersFilter: false,
    // It is unclear to me whether this is needed, or whether
    // showCapacity for PJM should be false. If anyone has a
    // better idea, please feel free to remove this variable.
    allowCapacityToggleInCostGraph: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER
};
const PJM_LOAD_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: LOAD_CAPACITY_COLOR_SLIDER
};
const MISO_CONFIG = {
    showCapacity: true,
    hasAllocatedCost: true,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    showChargingCostGraph: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER
};
const MISO_LOAD_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: LOAD_CAPACITY_COLOR_SLIDER
};

const SPP_CONFIG = {
    showCapacity: true,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER,
    hasPlannedInterconnectionLocations: true
};
const SPP_LOAD_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: LOAD_CAPACITY_COLOR_SLIDER
};

const ERCOT_CONFIG = {
    showModelSwitcherConstraintTable: true,
    showModelColumnConstraintTable: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showContingencyDetails: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER
};

const ERCOT_LOAD_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showContingencyDetails: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: LOAD_CAPACITY_COLOR_SLIDER
};

const WECC_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: true,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: true,
    showOutOfRegionBusFilter: true,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER,
    prioritizedScopeNames: WECC_PRIORITIZED_SCOPE_NAMES
};

const WECC_LOAD_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: true,
    showOutOfRegionBusFilter: true,
    capacityColorSliderConfig: LOAD_CAPACITY_COLOR_SLIDER
};

const TVA_CONFIG = {
    showCapacity: true,
    hasAllocatedCost: true,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: false,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: false,
    showOwnersFilter: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER
};

const TVA_STORAGE_CONFIG = {
    showCapacity: true,
    hasAllocatedCost: true,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: false,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    showChargingCostGraph: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER
};

const ISONE_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER
};

const SOCO_CONFIG = {
    showCapacity: true,
    hasAllocatedCost: true,
    isPreExistingConstraintsEnabled: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER
};

const SOCO_LOAD_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: LOAD_CAPACITY_COLOR_SLIDER
};

const DUKE_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: true,
    isPreExistingConstraintsEnabled: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: STANDARD_CAPACITY_COLOR_SLIDER,
    showImpactPercent: true
};

const DUKE_LOAD_CONFIG = {
    showCapacity: false,
    hasAllocatedCost: false,
    isPreExistingConstraintsEnabled: true,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: true,
    missingBranchMetadata: false,
    hideResourceType: false,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: LOAD_CAPACITY_COLOR_SLIDER
};

const CAISO_CONFIG = {
    showModelSwitcherConstraintTable: false,
    showModelColumnConstraintTable: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: false,
    missingBranchMetadata: false,
    hideResourceType: true,
    showChargingSize: true,
    showOwnersFilter: false,
    capacityColorSliderConfig: CAISO_CAPACITY_COLOR_SLIDER,
    noConstraintsDeliverable: CAISO_NO_CONSTRAINTS_DELIVERABLE,
    prioritizedScopeNames: CAISO_PRIORITIZED_SCOPE_NAMES
};

const CAISO_STORAGE_CONFIG = {
    showModelSwitcherConstraintTable: false,
    showModelColumnConstraintTable: false,
    allowUsersToViewLowConfidenceBuses: true,
    showContingency: true,
    showLineTaps: false,
    missingBranchMetadata: false,
    hideResourceType: true,
    showChargingSize: false,
    showOwnersFilter: false,
    capacityColorSliderConfig: CAISO_CAPACITY_COLOR_SLIDER,
    noConstraintsDeliverable: CAISO_NO_CONSTRAINTS_DELIVERABLE,
    prioritizedScopeNames: CAISO_PRIORITIZED_SCOPE_NAMES
};

const REGIONS_TO_COMPONENT_CONFIG: {
    readonly [key in RegionType]: {
        readonly [key in GeneratorType]: ComponentConfig | undefined;
    };
} = {
    NYISO: {
        [SOLAR_TYPE]: NYISO_CONFIG,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: NYISO_CONFIG,
        [LOAD_TYPE]: undefined
    },
    PJM: {
        [SOLAR_TYPE]: PJM_CONFIG,
        [WIND_TYPE]: PJM_CONFIG,
        [STORAGE_TYPE]: PJM_CONFIG,
        [LOAD_TYPE]: PJM_LOAD_CONFIG
    },
    MISO: {
        [SOLAR_TYPE]: MISO_CONFIG,
        [WIND_TYPE]: MISO_CONFIG,
        [STORAGE_TYPE]: MISO_CONFIG,
        [LOAD_TYPE]: MISO_LOAD_CONFIG
    },
    CAISO: {
        [SOLAR_TYPE]: CAISO_CONFIG,
        [WIND_TYPE]: CAISO_CONFIG,
        [STORAGE_TYPE]: CAISO_STORAGE_CONFIG,
        [LOAD_TYPE]: undefined
    },
    SPP: {
        [SOLAR_TYPE]: SPP_CONFIG,
        [WIND_TYPE]: SPP_CONFIG,
        [STORAGE_TYPE]: undefined,
        [LOAD_TYPE]: SPP_LOAD_CONFIG
    },
    ERCOT: {
        [SOLAR_TYPE]: ERCOT_CONFIG,
        [WIND_TYPE]: ERCOT_CONFIG,
        [STORAGE_TYPE]: ERCOT_CONFIG,
        [LOAD_TYPE]: ERCOT_LOAD_CONFIG
    },
    WECC: {
        [SOLAR_TYPE]: WECC_CONFIG,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: WECC_CONFIG,
        [LOAD_TYPE]: WECC_LOAD_CONFIG
    },
    TVA: {
        [SOLAR_TYPE]: TVA_CONFIG,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: TVA_STORAGE_CONFIG,
        [LOAD_TYPE]: undefined
    },
    ISONE: {
        [SOLAR_TYPE]: ISONE_CONFIG,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: ISONE_CONFIG,
        [LOAD_TYPE]: undefined
    },
    SOCO: {
        [SOLAR_TYPE]: SOCO_CONFIG,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: undefined,
        [LOAD_TYPE]: SOCO_LOAD_CONFIG
    },
    DUKE: {
        [SOLAR_TYPE]: DUKE_CONFIG,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: DUKE_CONFIG,
        [LOAD_TYPE]: DUKE_LOAD_CONFIG
    }
};
